import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, isAfter, isBefore, startOfDay } from 'date-fns';
export function DatePickerModal({
  open,
  onClose,
  selectedDate,
  onDateSelect,
  earliestDate,
  latestDate = new Date(),
  availableDates = [],
  onViewingMonthChange
}) {
  const [viewingMonth, setViewingMonth] = useState(selectedDate);
  
  // Update viewingMonth when selectedDate changes (when modal opens)
  useEffect(() => {
    if (open) {
      setViewingMonth(selectedDate);
    }
  }, [open, selectedDate]);
  
  const handleDateClick = date => {
    // Check if date is within range
    if (earliestDate && isBefore(date, startOfDay(earliestDate))) return;
    if (isAfter(date, startOfDay(latestDate))) return;
    onDateSelect(date);
    onClose();
  };
  const handlePrevMonth = () => {
    const newMonth = subMonths(viewingMonth, 1);
    setViewingMonth(newMonth);
    if (onViewingMonthChange) {
      onViewingMonthChange(newMonth);
    }
  };
  const handleNextMonth = () => {
    const newMonth = addMonths(viewingMonth, 1);
    setViewingMonth(newMonth);
    if (onViewingMonthChange) {
      onViewingMonthChange(newMonth);
    }
  };
  const handleToday = () => {
    const today = new Date();
    setViewingMonth(today);
    onDateSelect(today);
    onClose();
  };

  // Get all days in the viewing month
  const monthStart = startOfMonth(viewingMonth);
  const monthEnd = endOfMonth(viewingMonth);
  const daysInMonth = eachDayOfInterval({
    start: monthStart,
    end: monthEnd
  });

  // Get the day of week for the first day (0 = Sunday)
  const startDayOfWeek = monthStart.getDay();

  // Fill in empty cells before the month starts
  const emptyDays = Array(startDayOfWeek).fill(null);

  // Check if a date has data (pins)
  const hasData = date => {
    if (!availableDates || availableDates.length === 0) return false;
    return availableDates.some(availableDate => {
      // Handle both Date objects and date strings
      const compareDate = availableDate instanceof Date 
        ? availableDate 
        : new Date(availableDate);
      return isSameDay(compareDate, date);
    });
  };

  // Check if date is selectable
  const isSelectable = date => {
    if (earliestDate && isBefore(date, startOfDay(earliestDate))) return false;
    if (isAfter(date, startOfDay(latestDate))) return false;
    return true;
  };
  return <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{
    sx: {
      borderRadius: '12px',
      bgcolor: '#F2F2F2'
    }
  }}>
      <DialogTitle sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      p: 2,
      pb: 1
    }}>
        <Typography sx={{
        fontFamily: 'Roboto, sans-serif',
        fontSize: '18px',
        fontWeight: 600,
        color: '#FF8555'
      }}>
          Select Date
        </Typography>
        <IconButton onClick={onClose} sx={{
        color: '#4A5565',
        '&:hover': {
          bgcolor: '#FFE0D0'
        }
      }}>
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{
      p: 2
    }}>
        {/* Month Navigation */}
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
        pb: 1.5,
        borderBottom: '1px solid #E5E7EB'
      }}>
          <IconButton onClick={handlePrevMonth} sx={{
          bgcolor: 'white',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          width: '36px',
          height: '36px',
          '&:hover': {
            bgcolor: '#FFE0D0',
            borderColor: '#FF8555'
          }
        }}>
            <ChevronLeft size={18} color="#4A5565" />
          </IconButton>

          <Typography sx={{
          fontFamily: 'Roboto, sans-serif',
          fontSize: '16px',
          fontWeight: 600,
          color: '#2932E5'
        }}>
            {format(viewingMonth, 'MMMM yyyy')}
          </Typography>

          <IconButton onClick={handleNextMonth} disabled={isAfter(addMonths(viewingMonth, 1), latestDate)} sx={{
          bgcolor: 'white',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          width: '36px',
          height: '36px',
          '&:hover': {
            bgcolor: '#FFE0D0',
            borderColor: '#FF8555'
          },
          '&:disabled': {
            bgcolor: '#f9fafb',
            opacity: 0.5
          }
        }}>
            <ChevronRight size={18} color="#4A5565" />
          </IconButton>
        </Box>

        {/* Day Headers */}
        <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 0.5,
        mb: 1
      }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <Typography key={day} sx={{
          fontFamily: 'Roboto, sans-serif',
          fontSize: '12px',
          fontWeight: 600,
          color: '#9CA3AF',
          textAlign: 'center',
          py: 0.5
        }}>
              {day}
            </Typography>)}
        </Box>

        {/* Calendar Grid */}
        <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 0.5
      }}>
          {/* Empty cells before month starts */}
          {emptyDays.map((_, index) => <Box key={`empty-${index}`} sx={{
          aspectRatio: '1'
        }} />)}

          {/* Days in month */}
          {daysInMonth.map(date => {
          const isSelected = isSameDay(date, selectedDate);
          const isToday = isSameDay(date, new Date());
          const hasDataForDate = hasData(date);
          const isSelectableDate = isSelectable(date);
          return <Button key={date.toISOString()} onClick={() => handleDateClick(date)} disabled={!isSelectableDate} sx={{
            aspectRatio: '1',
            minWidth: 0,
            p: 0,
            bgcolor: isSelected ? '#2932E5' : hasDataForDate ? '#FFE0D0' : 'white',
            border: isToday ? '2px solid #FF8555' : '1px solid #E5E7EB',
            borderRadius: '8px',
            position: 'relative',
            '&:hover': {
              bgcolor: isSelected ? '#1f24b8' : '#FFE0D0',
              borderColor: '#FF8555'
            },
            '&:disabled': {
              bgcolor: '#f9fafb',
              opacity: 0.4,
              cursor: 'not-allowed'
            }
          }}>
                <Typography sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: isSelected ? 700 : hasDataForDate ? 600 : 400,
              color: isSelected ? 'white' : isSelectableDate ? '#4A5565' : '#9CA3AF'
            }}>
                  {format(date, 'd')}
                </Typography>

                {/* Indicator dot for dates with data */}
                {hasDataForDate && !isSelected && <Box sx={{
              position: 'absolute',
              bottom: 3,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '4px',
              height: '4px',
              borderRadius: '50%',
              bgcolor: '#FF8555'
            }} />}
              </Button>;
        })}
        </Box>

        {/* Legend */}
        <Box sx={{
        mt: 2,
        pt: 2,
        borderTop: '1px solid #E5E7EB',
        display: 'flex',
        gap: 2,
        flexWrap: 'wrap'
      }}>
            <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}>
              <Box sx={{
            width: '16px',
            height: '16px',
            bgcolor: '#FFE0D0',
            borderRadius: '4px',
            border: '1px solid #E5E7EB'
          }} />
              <Typography sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '11px',
            color: '#4A5565'
          }}>
                Has pins
              </Typography>
            </Box>

            <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}>
              <Box sx={{
            width: '16px',
            height: '16px',
            bgcolor: '#2932E5',
            borderRadius: '4px'
          }} />
              <Typography sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '11px',
            color: '#4A5565'
          }}>
                Selected
              </Typography>
            </Box>

            <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}>
              <Box sx={{
            width: '16px',
            height: '16px',
            bgcolor: 'white',
            borderRadius: '4px',
            border: '2px solid #FF8555'
          }} />
              <Typography sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '11px',
            color: '#4A5565'
          }}>
                Today
              </Typography>
            </Box>
          </Box>
      </DialogContent>

      <DialogActions sx={{
      p: 2,
      pt: 0
    }}>
        <Button onClick={handleToday} sx={{
        px: 2,
        py: 1,
        bgcolor: 'white',
        border: '1px solid #E5E7EB',
        color: '#2932E5',
        fontSize: '13px',
        fontWeight: 600,
        fontFamily: 'Roboto, sans-serif',
        textTransform: 'none',
        borderRadius: '8px',
        '&:hover': {
          bgcolor: '#FFE0D0',
          borderColor: '#FF8555'
        }
      }}>
          Jump to Today
        </Button>

        <Button onClick={onClose} sx={{
        px: 2,
        py: 1,
        bgcolor: '#2932E5',
        color: 'white',
        fontSize: '13px',
        fontWeight: 600,
        fontFamily: 'Roboto, sans-serif',
        textTransform: 'none',
        borderRadius: '8px',
        '&:hover': {
          bgcolor: '#1f24b8'
        }
      }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>;
}